import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ChevronDownIcon,
  EllipsisVerticalIcon,
  ChevronLeftIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { whatsappApi } from '../../api/whatsappApi';
import { auth } from '../../firebase';
import { getBaseUrl } from '../../api/apiClient';
import { buildWsBaseUrlFromHttp, WhatsappWsClient } from '../../modules/whatsapp/ws/WhatsappWsClient';
import { routeWhatsappWsEvent } from '../../modules/whatsapp/ws/WhatsappWsEventRouter';

const extractPlaceholderIndexes = (text) => {
  if (!text || typeof text !== 'string') return [];
  const indexes = new Set();
  const regex = /\{\{\s*(\d+)\s*\}\}/g;
  let match = regex.exec(text);
  while (match) {
    indexes.add(Number(match[1]));
    match = regex.exec(text);
  }
  return Array.from(indexes).sort((a, b) => a - b);
};

const buildExampleValueMap = (indexes, exampleRow) => {
  const map = {};
  if (!Array.isArray(exampleRow) || !exampleRow.length) {
    return map;
  }

  indexes.forEach((index, position) => {
    const byIndex = exampleRow[index - 1];
    const byPosition = exampleRow[position];
    const value = byIndex ?? byPosition;
    if (value != null) {
      map[index] = String(value);
    }
  });

  return map;
};

const buildTemplateRequirements = (template) => {
  if (!template || !Array.isArray(template.components)) {
    return {
      parameterFormat: '',
      headerFormat: '',
      headerMediaType: null,
      headerLocationRequired: false,
      headerTextPlaceholders: [],
      headerExampleTextParams: {},
      bodyPlaceholders: [],
      bodyExampleTextParams: {},
      bodyText: '',
      headerText: '',
      footerText: '',
    };
  }

  const components = template.components;
  const header = components.find((component) => String(component?.type || '').toUpperCase() === 'HEADER');
  const body = components.find((component) => String(component?.type || '').toUpperCase() === 'BODY');
  const footer = components.find((component) => String(component?.type || '').toUpperCase() === 'FOOTER');

  const headerFormat = String(header?.format || '').toUpperCase();
  const headerText = headerFormat === 'TEXT' ? String(header?.text || '') : '';
  const headerMediaType = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)
    ? headerFormat.toLowerCase()
    : null;
  const headerLocationRequired = headerFormat === 'LOCATION';

  const bodyText = String(body?.text || '');
  const footerText = String(footer?.text || '');

  const headerTextPlaceholders = extractPlaceholderIndexes(headerText);
  const bodyPlaceholders = extractPlaceholderIndexes(bodyText);

  const headerExampleRow = Array.isArray(header?.example?.header_text)
    ? header.example.header_text
    : [];
  const bodyExampleRow = Array.isArray(body?.example?.body_text?.[0])
    ? body.example.body_text[0]
    : [];

  return {
    parameterFormat: String(template.parameterFormat || ''),
    headerFormat,
    headerMediaType,
    headerLocationRequired,
    headerTextPlaceholders,
    headerExampleTextParams: buildExampleValueMap(headerTextPlaceholders, headerExampleRow),
    bodyPlaceholders,
    bodyExampleTextParams: buildExampleValueMap(bodyPlaceholders, bodyExampleRow),
    bodyText,
    headerText,
    footerText,
  };
};

const applyTemplatePreview = (text, values, fallbackValues = {}) => {
  if (!text) return '';
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, idx) => {
    const key = Number(idx);
    const value = values?.[key] || fallbackValues?.[key];
    return value && value.trim() ? value : `{{${idx}}}`;
  });
};

const asNonEmptyString = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed ? trimmed : '';
};

const normalizeStringList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);
};

const buildMessageFromWsPayload = (payload) => ({
  wamId: payload?.wamId || payload?.messageId || '',
  conversationId: payload?.conversationId || '',
  type: String(payload?.type || '').toLowerCase(),
  direction: String(payload?.direction || '').toLowerCase(),
  status: String(payload?.status || '').toUpperCase(),
  content: payload?.content && typeof payload.content === 'object' ? payload.content : {},
  contextMsgId: payload?.contextMsgId || null,
  errorCode: payload?.errorCode || null,
  errorMessage: payload?.errorMessage || null,
  timestamp: payload?.timestamp || new Date().toISOString(),
  createdAt: payload?.createdAt || null,
  updatedAt: payload?.updatedAt || null,
});

const createLocalMessageId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getSendResponseMessageMeta = (response) => {
  const message = Array.isArray(response?.messages) ? response.messages[0] : null;
  return {
    wamId: message?.id || '',
    status: String(message?.messageStatus || '').toUpperCase(),
  };
};

const getErrorText = (error, fallbackMessage) => {
  const responseMessage = error?.response?.data;
  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage.trim();
  }

  const nestedMessage = error?.response?.data?.message;
  if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
    return nestedMessage.trim();
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }

  return fallbackMessage;
};

const getMessageSignature = (message) => {
  const content = message?.content || {};
  const type = String(message?.type || '').toLowerCase();
  const contextMsgId = message?.contextMsgId || content?.contextMsgId || '';

  switch (type) {
    case 'text':
      return `text|${contextMsgId}|${asNonEmptyString(content?.text?.body)}`;
    case 'template':
      return `template|${contextMsgId}|${asNonEmptyString(content?.template?.name)}|${asNonEmptyString(content?.template?.rendered?.body || content?.template?.rendered?.fullText)}`;
    case 'image':
    case 'video':
    case 'audio':
    case 'document':
      return `${type}|${contextMsgId}|${asNonEmptyString(content?.[type]?.caption)}|${asNonEmptyString(content?.[type]?.filename)}`;
    default:
      return `${type}|${contextMsgId}`;
  }
};

const getConversationPreviewFromMessage = (message) => {
  const bubble = getMessageBubbleContent(message);
  return asNonEmptyString(bubble.body) || bubble.fallbackLabel || 'No preview available';
};

const buildOptimisticOutgoingMessage = ({
  conversationId,
  type,
  content,
  contextMsgId,
  timestamp,
  retryMeta,
}) => ({
  wamId: createLocalMessageId(),
  clientMessageId: createLocalMessageId(),
  conversationId: conversationId || '',
  type,
  direction: 'outbound',
  status: 'SENDING',
  content,
  contextMsgId: contextMsgId || null,
  errorCode: null,
  errorMessage: null,
  timestamp,
  createdAt: timestamp,
  updatedAt: timestamp,
  sendState: 'sending',
  retryMeta,
});

const getMessageBubbleContent = (message) => {
  const content = message?.content || {};
  const type = String(message?.type || content?.type || '').toLowerCase();

  const textBody = asNonEmptyString(content?.text?.body);
  if (textBody) {
    return {
      header: '',
      body: textBody,
      footer: '',
      buttons: [],
      fallbackLabel: '[text]',
    };
  }

  const template = content?.template;
  if (template && typeof template === 'object') {
    const rendered = template?.rendered && typeof template.rendered === 'object'
      ? template.rendered
      : {};

    const header = asNonEmptyString(rendered?.header);
    const body = asNonEmptyString(rendered?.body) || asNonEmptyString(rendered?.fullText);
    const footer = asNonEmptyString(rendered?.footer);
    const buttons = normalizeStringList(rendered?.buttons);

    if (header || body || footer || buttons.length) {
      return {
        header,
        body,
        footer,
        buttons,
        fallbackLabel: '[template]',
      };
    }

    return {
      header: '',
      body: asNonEmptyString(template?.name),
      footer: '',
      buttons: [],
      fallbackLabel: '[template]',
    };
  }

  const mediaFallbackByType = {
    image: '[image]',
    video: '[video]',
    audio: '[audio]',
    document: '[document]',
    sticker: '[sticker]',
    location: '[location]',
    contacts: '[contacts]',
    interactive: '[interactive]',
    reaction: '[reaction]',
  };

  const captionableTypes = ['image', 'video', 'document'];
  if (captionableTypes.includes(type)) {
    const media = content?.[type];
    const caption = asNonEmptyString(media?.caption);
    const fallbackLabel = mediaFallbackByType[type] || `[${type}]`;
    return {
      header: fallbackLabel,
      body: caption,
      footer: '',
      buttons: [],
      fallbackLabel,
    };
  }

  return {
    header: '',
    body: '',
    footer: '',
    buttons: [],
    fallbackLabel: mediaFallbackByType[type] || `[${type || 'message'}]`,
  };
};

const getConversationWindowState = (expirationTimestamp) => {
  if (!expirationTimestamp) {
    return {
      label: 'Only templates',
      isOpen: false,
      isUnknown: true,
    };
  }

  const expirationMs = new Date(expirationTimestamp).getTime();
  if (Number.isNaN(expirationMs)) {
    return {
      label: 'Only templates',
      isOpen: false,
      isUnknown: true,
    };
  }

  const remainingMs = expirationMs - Date.now();
  if (remainingMs <= 0) {
    return {
      label: 'Only templates',
      isOpen: false,
      isUnknown: false,
    };
  }

  const totalMinutes = Math.floor(remainingMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);

  return {
    label: `${parts.join(' ')} left`,
    isOpen: true,
    isUnknown: false,
  };
};

const ATTACHMENT_OPTIONS = [
  { type: 'image', label: 'Image', accept: 'image/*' },
  { type: 'video', label: 'Video', accept: 'video/*' },
  { type: 'audio', label: 'Audio', accept: 'audio/*' },
  { type: 'document', label: 'Document', accept: '.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx' },
];

const SWIPE_REPLY_TRIGGER_PX = 72;
const SWIPE_REPLY_MAX_OFFSET_PX = 96;
const CONVERSATIONS_PAGE_SIZE = 20;

const getReplyPreviewText = (message) => {
  const content = message?.content || {};
  const explicitPreview = asNonEmptyString(content?.contextMsgPreview);
  if (explicitPreview) {
    return explicitPreview;
  }

  const bubble = getMessageBubbleContent(message);
  return asNonEmptyString(bubble.body) || bubble.fallbackLabel;
};

const getInitials = (value) => {
  const text = String(value || '').trim();
  if (!text) return '?';

  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  const compact = text.replace(/[^a-zA-Z0-9]/g, '');
  if (!compact) return '?';
  return compact.slice(0, 2).toUpperCase();
};

const MessageStatusTicks = ({ messageStatus }) => {
  const isRead = messageStatus === 'read';

  return (
    <span
      className={isRead
        ? 'inline-flex text-sky-300 drop-shadow-[0_0_2px_rgba(14,165,233,0.9)]'
        : 'inline-flex text-emerald-100/70'}
      title={isRead ? 'Read' : 'Sent'}
      aria-label={isRead ? 'Read' : 'Sent'}
    >
      <svg
        viewBox="0 0 14 12"
        className="h-3.5 w-4"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.2 6.8L3.9 9.5L8.3 4.9"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.2 6.8L7.9 9.5L12.3 4.9"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
};

const SettingsModal = ({
  show,
  onClose,
  businessAccounts,
  selectedBusinessAccountId,
  onSelectBusinessAccountId,
  showAddAccountForm,
  onToggleAddAccountForm,
  addForm,
  onAddFormChange,
  onAddBusinessAccount,
  addingBusinessAccount,
  metaPhoneNumbers,
  loadingMetaPhoneNumbers,
  onLoadMetaPhoneNumbers,
  onPhoneDisplayNameChange,
  onSavePhoneNumber,
  savingPhoneNumberId,
  metaTemplates,
  loadingMetaTemplates,
  onLoadMetaTemplates,
  onSaveTemplate,
  savingTemplateId,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">WhatsApp Settings</h2>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            Close
          </button>
        </div>

        <div className="space-y-6 p-6">
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-700">Business Account</h3>
              <button
                onClick={onToggleAddAccountForm}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                title="Add business account"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>

            <select
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
              value={selectedBusinessAccountId}
              onChange={(e) => onSelectBusinessAccountId(e.target.value)}
            >
              {businessAccounts.length === 0 && <option value="">No business accounts</option>}
              {businessAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>

            {showAddAccountForm && (
              <div className="mt-4 grid gap-3 border-t border-gray-200 pt-4">
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Business ID"
                  value={addForm.businessId}
                  onChange={(e) => onAddFormChange('businessId', e.target.value)}
                />
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Business Name"
                  value={addForm.name}
                  onChange={(e) => onAddFormChange('name', e.target.value)}
                />
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Timezone ID (e.g. Europe/Bucharest)"
                  value={addForm.timezoneId}
                  onChange={(e) => onAddFormChange('timezoneId', e.target.value)}
                />
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Currency (optional)"
                  value={addForm.currency}
                  onChange={(e) => onAddFormChange('currency', e.target.value)}
                />
                <button
                  onClick={onAddBusinessAccount}
                  disabled={addingBusinessAccount}
                  className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {addingBusinessAccount ? 'Saving...' : 'Save Business Account'}
                </button>
              </div>
            )}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Phone Numbers</h3>
                <button
                  onClick={onLoadMetaPhoneNumbers}
                  disabled={loadingMetaPhoneNumbers || !selectedBusinessAccountId}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {loadingMetaPhoneNumbers ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {!selectedBusinessAccountId && (
                <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                  Select a business account first.
                </div>
              )}

              {selectedBusinessAccountId && (
                <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                  {metaPhoneNumbers.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                      No phone numbers loaded yet.
                    </div>
                  )}

                  {metaPhoneNumbers.map((phone) => (
                    <div
                      key={phone.id}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-800">Phone Number: {phone.displayPhoneNumber || phone.id}</div>
                          <div className="text-xs text-gray-500">Phone ID: {phone.id}</div>
                          <div className="mt-2">
                            <label className="mb-1 block text-xs font-medium text-gray-600">Display Name</label>
                            <input
                              className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900"
                              value={phone.verifiedName || ''}
                              onChange={(e) => onPhoneDisplayNameChange(phone.id, e.target.value)}
                              placeholder="Enter display name"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => onSavePhoneNumber(phone)}
                          disabled={savingPhoneNumberId === phone.id}
                          className="mt-0.5 rounded-lg bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {savingPhoneNumberId === phone.id ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Templates</h3>
                <button
                  onClick={onLoadMetaTemplates}
                  disabled={loadingMetaTemplates || !selectedBusinessAccountId}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {loadingMetaTemplates ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {!selectedBusinessAccountId && (
                <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                  Select a business account first.
                </div>
              )}

              {selectedBusinessAccountId && (
                <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                  {metaTemplates.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                      No templates loaded yet.
                    </div>
                  )}

                  {metaTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-800">{template.name}</div>
                        <div className="text-xs text-gray-500">
                          {template.language || 'unknown'} • {template.status || 'unknown'}
                        </div>
                      </div>
                      <button
                        onClick={() => onSaveTemplate(template)}
                        disabled={savingTemplateId === template.id}
                        className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {savingTemplateId === template.id ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StartConversationModal = ({
  show,
  onClose,
  form,
  onFormChange,
  onSubmit,
  submitting,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Start New Conversation</h2>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            disabled={submitting}
          >
            Close
          </button>
        </div>

        <div className="space-y-4 p-6">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Recipient Phone Number</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900"
              placeholder="e.g. +40740123456"
              value={form.to}
              onChange={(e) => onFormChange('to', e.target.value)}
              disabled={submitting}
            />
            <p className="mt-1 text-xs text-gray-500">Use international format with country code (for example +40...).</p>
          </div>

          <button
            onClick={onSubmit}
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? 'Opening...' : 'Open Conversation Panel'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SendTemplateModal = ({
  show,
  onClose,
  templates,
  selectedTemplateId,
  onSelectTemplateId,
  selectedTemplate,
  requirements,
  templateInputs,
  onTemplateInputChange,
  missingRequiredInputs,
  onSubmit,
  submitting,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Send Template</h2>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            disabled={submitting}
          >
            Close
          </button>
        </div>

        <div className="space-y-4 p-6">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Template</label>
            <select
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
              value={selectedTemplateId}
              onChange={(e) => onSelectTemplateId(e.target.value)}
              disabled={submitting}
            >
              <option value="">Select template</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.language || 'unknown'})
                </option>
              ))}
            </select>
          </div>

          {selectedTemplate && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Template Inputs</div>

              <div className="text-xs text-gray-500">
                Parameter format: {requirements.parameterFormat || 'POSITIONAL'}
              </div>

              {requirements.headerLocationRequired && (
                <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-600">Header location</div>
                  <input
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    placeholder="Latitude (required)"
                    value={templateInputs.headerLocation.latitude}
                    onChange={(e) => onTemplateInputChange('headerLocation', e.target.value, 'latitude')}
                    disabled={submitting}
                  />
                  <input
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    placeholder="Longitude (required)"
                    value={templateInputs.headerLocation.longitude}
                    onChange={(e) => onTemplateInputChange('headerLocation', e.target.value, 'longitude')}
                    disabled={submitting}
                  />
                  <input
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    placeholder="Location name (optional)"
                    value={templateInputs.headerLocation.name}
                    onChange={(e) => onTemplateInputChange('headerLocation', e.target.value, 'name')}
                    disabled={submitting}
                  />
                  <input
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    placeholder="Address (optional)"
                    value={templateInputs.headerLocation.address}
                    onChange={(e) => onTemplateInputChange('headerLocation', e.target.value, 'address')}
                    disabled={submitting}
                  />
                </div>
              )}

              {requirements.headerMediaType && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Header {requirements.headerMediaType} URL
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    placeholder={`https://... (${requirements.headerMediaType})`}
                    value={templateInputs.headerMediaLink}
                    onChange={(e) => onTemplateInputChange('headerMediaLink', e.target.value)}
                    disabled={submitting}
                  />
                </div>
              )}

              {requirements.headerTextPlaceholders.map((index) => (
                <div key={`header-${index}`}>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Header Variable {index}</label>
                  <input
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    value={templateInputs.headerTextParams[index] || ''}
                    onChange={(e) => onTemplateInputChange('headerTextParam', e.target.value, index)}
                    placeholder={requirements.headerExampleTextParams[index] || ''}
                    disabled={submitting}
                  />
                </div>
              ))}

              {requirements.bodyPlaceholders.map((index) => (
                <div key={`body-${index}`}>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Body Variable {index}</label>
                  <input
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    value={templateInputs.bodyTextParams[index] || ''}
                    onChange={(e) => onTemplateInputChange('bodyTextParam', e.target.value, index)}
                    placeholder={requirements.bodyExampleTextParams[index] || ''}
                    disabled={submitting}
                  />
                </div>
              ))}
            </div>
          )}

          {selectedTemplate && (
            <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Preview</div>

              {requirements.headerMediaType && (
                <div className="text-sm text-gray-700">
                  Header {requirements.headerMediaType}: {templateInputs.headerMediaLink || '[missing]'}
                </div>
              )}

              {requirements.headerLocationRequired && (
                <div className="text-sm text-gray-700">
                  Header location: {templateInputs.headerLocation.latitude || '[lat]'}, {templateInputs.headerLocation.longitude || '[lng]'}
                </div>
              )}

              {requirements.headerText && (
                <div className="text-sm text-gray-700">
                  Header: {applyTemplatePreview(requirements.headerText, templateInputs.headerTextParams, requirements.headerExampleTextParams)}
                </div>
              )}

              <div className="text-sm text-gray-800">
                {requirements.bodyText
                  ? applyTemplatePreview(requirements.bodyText, templateInputs.bodyTextParams, requirements.bodyExampleTextParams)
                  : 'No body text preview available'}
              </div>

              {requirements.footerText && (
                <div className="text-xs text-gray-500">Footer: {requirements.footerText}</div>
              )}

              {missingRequiredInputs && (
                <div className="text-xs text-amber-600">Complete all required template inputs before sending.</div>
              )}
            </div>
          )}

          <button
            onClick={onSubmit}
            disabled={submitting || !selectedTemplateId || missingRequiredInputs}
            className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? 'Sending...' : 'Send Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Whatsapp = ({ deepLinkPhoneNumber = '' }) => {
  const [loading, setLoading] = useState(false);
  const [businessAccounts, setBusinessAccounts] = useState([]);
  const [selectedBusinessAccountId, setSelectedBusinessAccountId] = useState('');

  const [savedPhoneNumbers, setSavedPhoneNumbers] = useState([]);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState('');

  const [conversations, setConversations] = useState([]);
  const [conversationsPage, setConversationsPage] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState('');
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [conversationSearch, setConversationSearch] = useState('');
  const [editingConversationProfileName, setEditingConversationProfileName] = useState(false);
  const [conversationProfileNameDraft, setConversationProfileNameDraft] = useState('');
  const [savingConversationProfileName, setSavingConversationProfileName] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [pendingAttachmentType, setPendingAttachmentType] = useState('');
  const [pendingAttachmentPreview, setPendingAttachmentPreview] = useState(null);
  const [activeMessageMenuId, setActiveMessageMenuId] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [touchStartByMessageId, setTouchStartByMessageId] = useState({});
  const [swipeOffsetByMessageId, setSwipeOffsetByMessageId] = useState({});

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    businessId: '',
    name: '',
    timezoneId: '',
    currency: '',
    messageTemplateNamespace: '',
  });

  const [metaPhoneNumbers, setMetaPhoneNumbers] = useState([]);
  const [loadingMetaPhoneNumbers, setLoadingMetaPhoneNumbers] = useState(false);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [loadingMetaTemplates, setLoadingMetaTemplates] = useState(false);
  const [addingBusinessAccount, setAddingBusinessAccount] = useState(false);
  const [showAddAccountForm, setShowAddAccountForm] = useState(false);
  const [startConversationOpen, setStartConversationOpen] = useState(false);
  const [startConversationForm, setStartConversationForm] = useState({ to: '' });
  const [manualRecipientWaId, setManualRecipientWaId] = useState('');
  const [sendTemplateOpen, setSendTemplateOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState('list');
  const [mobileChatMenuOpen, setMobileChatMenuOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templateInputs, setTemplateInputs] = useState({
    headerMediaLink: '',
    headerLocation: {
      latitude: '',
      longitude: '',
      name: '',
      address: '',
    },
    headerTextParams: {},
    bodyTextParams: {},
  });
  const [savingPhoneNumberId, setSavingPhoneNumberId] = useState('');
  const [savingTemplateId, setSavingTemplateId] = useState('');
  const attachmentInputRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const deepLinkHandledRef = useRef('');
  const markedReadMessageByConversationRef = useRef({});
  const wsClientRef = useRef(null);
  const wsEventCacheRef = useRef(new Map());
  const selectedPhoneNumberIdRef = useRef('');
  const selectedConversationIdRef = useRef('');
  const conversationSearchRef = useRef('');
  const loadConversationsTimerRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pendingConversationScrollRef = useRef(false);

  const updateConversationPreview = useCallback((conversationId, nextMessage, fallbackWaId = '') => {
    if (!conversationId) {
      return;
    }

    const preview = getConversationPreviewFromMessage(nextMessage);
    const nextUpdatedAt = nextMessage?.timestamp || nextMessage?.createdAt || new Date().toISOString();

    setConversations((prev) => {
      const index = prev.findIndex((conversation) => conversation.id === conversationId);
      if (index === -1) {
        return prev;
      }

      const updatedConversation = {
        ...prev[index],
        waId: prev[index]?.waId || fallbackWaId,
        lastMessagePreview: preview,
        updatedAt: nextUpdatedAt,
      };

      const next = [...prev];
      next.splice(index, 1);
      return [updatedConversation, ...next];
    });
  }, []);

  const reconcileOptimisticMessage = useCallback((clientMessageId, responseMeta = {}) => {
    setMessages((prev) => {
      const nextWamId = responseMeta?.wamId || '';
      const nextStatus = String(responseMeta?.status || 'SENT').toUpperCase();
      const tempIndex = prev.findIndex((message) => message.clientMessageId === clientMessageId);
      const actualIndex = nextWamId ? prev.findIndex((message) => message.wamId === nextWamId) : -1;

      if (tempIndex === -1 && actualIndex === -1) {
        return prev;
      }

      if (tempIndex !== -1 && actualIndex !== -1 && tempIndex !== actualIndex) {
        const next = [...prev];
        next[actualIndex] = {
          ...next[tempIndex],
          ...next[actualIndex],
          wamId: nextWamId,
          status: nextStatus,
          sendState: 'sent',
          errorCode: null,
          errorMessage: null,
          retryMeta: null,
        };
        next.splice(tempIndex, 1);
        return next.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      }

      const targetIndex = actualIndex !== -1 ? actualIndex : tempIndex;
      const next = [...prev];
      next[targetIndex] = {
        ...next[targetIndex],
        wamId: nextWamId || next[targetIndex].wamId,
        status: nextStatus,
        sendState: nextWamId ? 'sent' : next[targetIndex].sendState,
        errorCode: null,
        errorMessage: null,
        retryMeta: nextWamId ? null : next[targetIndex].retryMeta,
      };
      return next.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });
  }, []);

  const markOptimisticMessageAsFailed = useCallback((clientMessageId, error) => {
    const errorText = getErrorText(error, 'Failed to send message');

    setMessages((prev) => prev.map((message) => {
      if (message.clientMessageId !== clientMessageId) {
        return message;
      }

      return {
        ...message,
        status: 'FAILED',
        sendState: 'failed',
        errorMessage: errorText,
      };
    }));
  }, []);

  const markOptimisticMessageAsSending = useCallback((clientMessageId) => {
    setMessages((prev) => prev.map((message) => {
      if (message.clientMessageId !== clientMessageId) {
        return message;
      }

      return {
        ...message,
        status: 'SENDING',
        sendState: 'sending',
        errorCode: null,
        errorMessage: null,
      };
    }));
  }, []);

  const appendOptimisticMessage = useCallback((message, fallbackWaId = '') => {
    setMessages((prev) => [...prev, message].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    updateConversationPreview(message.conversationId, message, fallbackWaId);
    return message.clientMessageId;
  }, [updateConversationPreview]);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  const latestMessageScrollKey = useMemo(() => {
    const latestMessage = messages[messages.length - 1];
    if (!latestMessage) {
      return '';
    }

    return [
      messages.length,
      latestMessage.wamId,
      latestMessage.clientMessageId,
      latestMessage.timestamp,
    ].join('|');
  }, [messages]);

  const scrollMessagesToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollTop = container.scrollHeight;
  }, []);

  const selectedConversationWindowState = useMemo(
    () => getConversationWindowState(selectedConversation?.expirationTimestamp),
    [selectedConversation?.expirationTimestamp]
  );

  const canSendAnyMessage = Boolean(selectedConversation && selectedConversationWindowState.isOpen);

  const selectedTemplate = useMemo(
    () => savedTemplates.find((template) => template.id === selectedTemplateId) || null,
    [savedTemplates, selectedTemplateId]
  );

  const templateRequirements = useMemo(
    () => buildTemplateRequirements(selectedTemplate),
    [selectedTemplate]
  );

  const missingRequiredTemplateInputs = useMemo(() => {
    if (!selectedTemplateId) return false;

    if (templateRequirements.headerMediaType && !templateInputs.headerMediaLink.trim()) {
      return true;
    }

    if (templateRequirements.headerLocationRequired) {
      if (!String(templateInputs.headerLocation.latitude || '').trim()) {
        return true;
      }
      if (!String(templateInputs.headerLocation.longitude || '').trim()) {
        return true;
      }
    }

    if (templateRequirements.headerTextPlaceholders.some((index) => !String(templateInputs.headerTextParams[index] || '').trim())) {
      return true;
    }

    if (templateRequirements.bodyPlaceholders.some((index) => !String(templateInputs.bodyTextParams[index] || '').trim())) {
      return true;
    }

    return false;
  }, [selectedTemplateId, templateRequirements, templateInputs]);

  const filteredConversations = conversations;

  const loadBusinessAccounts = async () => {
    setLoading(true);
    try {
      const data = await whatsappApi.listBusinessAccounts();
      const list = Array.isArray(data) ? data : [];
      setBusinessAccounts(list);

      if (list.length > 0 && !selectedBusinessAccountId) {
        setSelectedBusinessAccountId(list[0].id);
      }
    } catch (error) {
      console.error('Failed to load WhatsApp business accounts', error);
      toast.error('Failed to load WhatsApp business accounts');
      setBusinessAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedPhoneNumbers = async (businessAccountId) => {
    if (!businessAccountId) {
      setSavedPhoneNumbers([]);
      setSelectedPhoneNumberId('');
      return;
    }

    try {
      const data = await whatsappApi.listSavedPhoneNumbers(businessAccountId);
      const list = Array.isArray(data) ? data : [];
      setSavedPhoneNumbers(list);

      if (list.length > 0) {
        setSelectedPhoneNumberId((prev) => (list.some((item) => item.id === prev) ? prev : list[0].id));
      } else {
        setSelectedPhoneNumberId('');
      }
    } catch (error) {
      console.error('Failed to load saved phone numbers', error);
      toast.error('Failed to load saved phone numbers');
      setSavedPhoneNumbers([]);
      setSelectedPhoneNumberId('');
    }
  };

  const loadConversations = useCallback(async (phoneNumberId, search = '', page = 0, options = {}) => {
    const { append = false } = options;

    if (!phoneNumberId) {
      setConversations([]);
      setConversationsPage(0);
      setHasMoreConversations(false);
      return [];
    }

    if (append) {
      setLoadingMoreConversations(true);
    } else {
      setConversationsLoading(true);
    }

    try {
      const data = await whatsappApi.listConversations(phoneNumberId, search, page, CONVERSATIONS_PAGE_SIZE);
      const list = Array.isArray(data) ? data : [];

      setConversations((prev) => {
        if (!append || page === 0) {
          return list;
        }

        const merged = [...prev];
        list.forEach((conversation) => {
          const existingIndex = merged.findIndex((item) => item.id === conversation.id);
          if (existingIndex === -1) {
            merged.push(conversation);
          } else {
            merged[existingIndex] = {
              ...merged[existingIndex],
              ...conversation,
            };
          }
        });
        return merged;
      });

      setConversationsPage(page);
      setHasMoreConversations(list.length >= CONVERSATIONS_PAGE_SIZE);
      return list;
    } catch (error) {
      console.error('Failed to load conversations', error);
      toast.error('Failed to load conversations');
      if (!append || page === 0) {
        setConversations([]);
        setConversationsPage(0);
        setHasMoreConversations(false);
      }
      return [];
    } finally {
      if (append) {
        setLoadingMoreConversations(false);
      } else {
        setConversationsLoading(false);
      }
    }
  }, []);

  const handleConversationsScroll = useCallback((event) => {
    if (!selectedPhoneNumberId || conversationsLoading || loadingMoreConversations || !hasMoreConversations) {
      return;
    }

    const container = event.currentTarget;
    const thresholdPx = 48;
    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceToBottom > thresholdPx) {
      return;
    }

    loadConversations(selectedPhoneNumberId, conversationSearch.trim(), conversationsPage + 1, { append: true });
  }, [
    selectedPhoneNumberId,
    conversationsLoading,
    loadingMoreConversations,
    hasMoreConversations,
    conversationSearch,
    conversationsPage,
    loadConversations,
  ]);

  const loadConversationMessages = useCallback(async (conversationId) => {
    if (!conversationId) {
      setMessages([]);
      return;
    }

    setMessagesLoading(true);
    try {
      const data = await whatsappApi.listConversationMessages(conversationId, 0, 50);
      const list = Array.isArray(data) ? data : [];
      const sortedAsc = [...list].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setMessages(sortedAsc);

      const latestInboundMessage = [...sortedAsc]
        .reverse()
        .find((message) => String(message?.direction || '').toLowerCase() === 'inbound');

      const latestInboundMessageId = latestInboundMessage?.wamId;
      if (!selectedPhoneNumberId || !latestInboundMessageId) {
        return;
      }

      if (markedReadMessageByConversationRef.current[conversationId] === latestInboundMessageId) {
        return;
      }

      try {
        await whatsappApi.markMessageAsRead({
          phoneNumberId: selectedPhoneNumberId,
          messageId: latestInboundMessageId,
        });
        markedReadMessageByConversationRef.current[conversationId] = latestInboundMessageId;
      } catch (markReadError) {
        console.error('Failed to mark WhatsApp message as read', markReadError);
      }
    } catch (error) {
      console.error('Failed to load conversation messages', error);
      toast.error('Failed to load conversation messages');
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [selectedPhoneNumberId]);

  const scheduleConversationRefresh = useCallback((phoneNumberId, search = '') => {
    if (!phoneNumberId) {
      return;
    }

    if (loadConversationsTimerRef.current) {
      clearTimeout(loadConversationsTimerRef.current);
    }

    loadConversationsTimerRef.current = setTimeout(() => {
      loadConversations(phoneNumberId, search, 0);
    }, 250);
  }, [loadConversations]);

  const isDuplicateWsEvent = useCallback((event) => {
    const payload = event?.payload && typeof event.payload === 'object' ? event.payload : {};
    const messageId = payload?.messageId || '';
    const conversationId = payload?.conversationId || '';
    const readAt = payload?.readAt || '';
    const eventKey = `${event?.type || 'UNKNOWN'}|${messageId}|${conversationId}|${readAt}|${event?.createdAt || ''}`;
    const now = Date.now();

    for (const [key, value] of wsEventCacheRef.current.entries()) {
      if (now - value > 60000) {
        wsEventCacheRef.current.delete(key);
      }
    }

    if (wsEventCacheRef.current.has(eventKey)) {
      return true;
    }

    wsEventCacheRef.current.set(eventKey, now);
    return false;
  }, []);

  const handleWhatsappWsEvent = useCallback((event) => {
    if (!event || typeof event !== 'object') {
      return;
    }

    if (isDuplicateWsEvent(event)) {
      return;
    }

    routeWhatsappWsEvent(event, {
      onConversationCreated: ({ event: wsEvent, payload }) => {
        const conversationId = payload?.conversationId;
        if (conversationId) {
          setConversations((prev) => {
            const idx = prev.findIndex((conversation) => conversation.id === conversationId);
            const nextConversation = {
              id: conversationId,
              waId: payload?.waId || '',
              profileName: payload?.profileName || payload?.waId || '',
              originType: payload?.originType || null,
              updatedAt: wsEvent?.createdAt || new Date().toISOString(),
              lastMessagePreview: 'New conversation',
            };

            if (idx === -1) {
              return [nextConversation, ...prev];
            }

            const clone = [...prev];
            clone[idx] = {
              ...clone[idx],
              ...nextConversation,
            };
            return clone;
          });
        }

        const phoneNumberId = selectedPhoneNumberIdRef.current;
        if (phoneNumberId) {
          scheduleConversationRefresh(phoneNumberId, conversationSearchRef.current.trim());
        }
      },
      onMessageCreated: ({ payload }) => {
        const conversationId = payload?.conversationId;
        const phoneNumberId = selectedPhoneNumberIdRef.current;

        if (phoneNumberId) {
          scheduleConversationRefresh(phoneNumberId, conversationSearchRef.current.trim());
        }

        if (conversationId && conversationId === selectedConversationIdRef.current) {
          const nextMessage = buildMessageFromWsPayload(payload);
          if (!nextMessage.wamId) {
            return;
          }

          setMessages((prev) => {
            const exactMatchIndex = prev.findIndex((message) => message.wamId === nextMessage.wamId);
            if (exactMatchIndex !== -1) {
              const next = [...prev];
              next[exactMatchIndex] = {
                ...next[exactMatchIndex],
                ...nextMessage,
                sendState: 'sent',
                retryMeta: null,
                errorCode: null,
                errorMessage: null,
              };
              return next.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            }

            const optimisticMatchIndex = prev.findIndex((message) => (
              message.sendState === 'sending'
              && message.direction === 'outbound'
              && message.conversationId === nextMessage.conversationId
              && getMessageSignature(message) === getMessageSignature(nextMessage)
            ));

            if (optimisticMatchIndex !== -1) {
              const next = [...prev];
              next[optimisticMatchIndex] = {
                ...next[optimisticMatchIndex],
                ...nextMessage,
                clientMessageId: next[optimisticMatchIndex].clientMessageId,
                sendState: 'sent',
                retryMeta: null,
                errorCode: null,
                errorMessage: null,
              };
              return next.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            }

            return [...prev, nextMessage].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          });

          updateConversationPreview(conversationId, nextMessage, payload?.waId || '');
        }
      },
      onMessageRead: ({ payload }) => {
        const messageId = payload?.messageId;
        if (!messageId) {
          return;
        }

        setMessages((prev) => prev.map((message) => {
          if (message.wamId !== messageId) {
            return message;
          }
          return {
            ...message,
            status: 'READ',
            readAt: payload?.readAt || message?.readAt,
          };
        }));
      },
      onUnknown: ({ event: wsEvent }) => {
        console.debug('[WA-WS] Unknown event type', wsEvent?.type);
      },
      onInvalidEvent: (invalidEvent) => {
        console.debug('[WA-WS] Invalid event payload', invalidEvent);
      },
    });
  }, [isDuplicateWsEvent, scheduleConversationRefresh, updateConversationPreview]);

  const loadSavedTemplates = async (businessAccountId) => {
    if (!businessAccountId) {
      setSavedTemplates([]);
      return;
    }
    try {
      const data = await whatsappApi.listSavedTemplates(businessAccountId);
      setSavedTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load saved templates', error);
      setSavedTemplates([]);
    }
  };

  useEffect(() => {
    loadBusinessAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBusinessAccountId) {
      loadSavedPhoneNumbers(selectedBusinessAccountId);
      loadSavedTemplates(selectedBusinessAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessAccountId]);

  useEffect(() => {
    if (!selectedPhoneNumberId) {
      setConversations([]);
      setSelectedConversationId('');
      setMessages([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      loadConversations(selectedPhoneNumberId, conversationSearch.trim(), 0);
    }, 250);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhoneNumberId, conversationSearch]);

  useEffect(() => {
    if (!conversations.length) {
      setSelectedConversationId('');
      setMessages([]);
      return;
    }

    // Keep manual recipient mode active when opening a new conversation panel.
    if (manualRecipientWaId) {
      setSelectedConversationId('');
      setMessages([]);
      return;
    }

    if (!conversations.some((item) => item.id === selectedConversationId)) {
      setSelectedConversationId(conversations[0].id);
    }
  }, [conversations, selectedConversationId, manualRecipientWaId]);

  useEffect(() => {
    if (selectedConversationId) {
      pendingConversationScrollRef.current = true;
      loadConversationMessages(selectedConversationId);
    } else {
      pendingConversationScrollRef.current = false;
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId]);

  useEffect(() => {
    setReplyingToMessage(null);
    setActiveMessageMenuId('');
    setMobileChatMenuOpen(false);
  }, [selectedConversationId]);

  useLayoutEffect(() => {
    if (!selectedConversationId || !latestMessageScrollKey) {
      return;
    }

    scrollMessagesToBottom();
  }, [selectedConversationId, latestMessageScrollKey, scrollMessagesToBottom]);

  useLayoutEffect(() => {
    if (!selectedConversationId || messagesLoading || !pendingConversationScrollRef.current) {
      return;
    }

    pendingConversationScrollRef.current = false;
    scrollMessagesToBottom();
  }, [selectedConversationId, messagesLoading, messages.length, scrollMessagesToBottom]);

  useEffect(() => {
    setTemplateInputs({
      headerMediaLink: '',
      headerLocation: {
        latitude: '',
        longitude: '',
        name: '',
        address: '',
      },
      headerTextParams: templateRequirements.headerExampleTextParams,
      bodyTextParams: templateRequirements.bodyExampleTextParams,
    });
  }, [selectedTemplateId, templateRequirements]);

  useEffect(() => {
    if (!deepLinkPhoneNumber || !selectedPhoneNumberId) {
      return;
    }

    const normalizedPathNumber = normalizePhone(deepLinkPhoneNumber);
    if (!normalizedPathNumber || deepLinkHandledRef.current === normalizedPathNumber) {
      return;
    }

    deepLinkHandledRef.current = normalizedPathNumber;
    handleStartConversationByPhone(deepLinkPhoneNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkPhoneNumber, selectedPhoneNumberId]);

  useEffect(() => {
    selectedPhoneNumberIdRef.current = selectedPhoneNumberId;
  }, [selectedPhoneNumberId]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  useEffect(() => {
    conversationSearchRef.current = conversationSearch;
  }, [conversationSearch]);

  useEffect(() => {
    const wsBaseUrl = buildWsBaseUrlFromHttp(getBaseUrl());

    const client = new WhatsappWsClient({
      wsBaseUrl,
      getFirebaseToken: async (forceRefresh = false) => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('Missing authenticated Firebase user');
        }
        return currentUser.getIdToken(forceRefresh);
      },
      onEvent: handleWhatsappWsEvent,
      onOpen: () => {},
      onClose: () => {},
      onReconnectFailed: () => {
        toast.error('WhatsApp realtime connection reconnect was unsuccessful.');
      },
      maxReconnectAttempts: 5,
      onError: (error) => {
        console.error('[WA-WS] socket error', error);
      },
    });

    wsClientRef.current = client;
    client.connect().catch((error) => {
      console.error('[WA-WS] connect failed', error);
      client.scheduleReconnect();
    });

    return () => {
      client.close();
      wsClientRef.current = null;

      if (loadConversationsTimerRef.current) {
        clearTimeout(loadConversationsTimerRef.current);
        loadConversationsTimerRef.current = null;
      }
    };
  }, [handleWhatsappWsEvent]);

  const handleAddFormChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddBusinessAccount = async () => {
    if (!addForm.businessId || !addForm.name || !addForm.timezoneId) {
      toast.error('Business ID, Name and Timezone are required');
      return;
    }

    setAddingBusinessAccount(true);
    try {
      await whatsappApi.addBusinessAccount(addForm);
      toast.success('Business account saved');
      setAddForm({
        businessId: '',
        name: '',
        timezoneId: '',
        currency: '',
        messageTemplateNamespace: '',
      });
      setShowAddAccountForm(false);
      await loadBusinessAccounts();
    } catch (error) {
      console.error('Failed to save business account', error);
      toast.error('Failed to save business account');
    } finally {
      setAddingBusinessAccount(false);
    }
  };

  const handleLoadMetaPhoneNumbers = async () => {
    if (!selectedBusinessAccountId) {
      toast.error('Select a business account first');
      return;
    }

    setLoadingMetaPhoneNumbers(true);
    try {
      const response = await whatsappApi.listMetaPhoneNumbers(selectedBusinessAccountId, 50);
      const list = Array.isArray(response?.data) ? response.data : [];
      setMetaPhoneNumbers(
        list.map((item) => ({
          id: item.id,
          verifiedName: item.verifiedName || item.verified_name || '',
          originalVerifiedName: item.verifiedName || item.verified_name || '',
          displayPhoneNumber: item.displayPhoneNumber || item.display_phone_number || '',
          qualityRating: item.qualityRating || item.quality_rating,
          platformType: item.platformType || item.platform_type,
          codeVerificationStatus: item.codeVerificationStatus || item.code_verification_status,
          throughput: item.throughput,
          webhookConfiguration: item.webhookConfiguration ?? item.webhook_configuration,
        }))
      );
    } catch (error) {
      console.error('Failed to load Meta phone numbers', error);
      toast.error('Failed to load Meta phone numbers');
      setMetaPhoneNumbers([]);
    } finally {
      setLoadingMetaPhoneNumbers(false);
    }
  };

  const handleSavePhoneNumber = async (phone) => {
    setSavingPhoneNumberId(phone.id);
    try {
      await whatsappApi.savePhoneNumber({
        businessAccountId: selectedBusinessAccountId,
        id: phone.id,
        verifiedName: (phone.verifiedName || '').trim() || phone.originalVerifiedName || phone.displayPhoneNumber || phone.id,
        displayPhoneNumber: phone.displayPhoneNumber || phone.id,
        qualityRating: phone.qualityRating || 'UNKNOWN',
        platformType: phone.platformType || 'UNKNOWN',
        codeVerificationStatus: phone.codeVerificationStatus || 'UNKNOWN',
        throughput: phone.throughput || {},
        webhookConfiguration: phone.webhookConfiguration || {},
      });

      toast.success('Phone number saved');
      await loadSavedPhoneNumbers(selectedBusinessAccountId);
    } catch (error) {
      console.error('Failed to save phone number', error);
      toast.error(error.message || 'Failed to save phone number');
    } finally {
      setSavingPhoneNumberId('');
    }
  };

  const handlePhoneDisplayNameChange = (phoneId, value) => {
    setMetaPhoneNumbers((prev) => prev.map((phone) => (
      phone.id === phoneId ? { ...phone, verifiedName: value } : phone
    )));
  };

  const handleLoadMetaTemplates = async () => {
    if (!selectedBusinessAccountId) {
      toast.error('Select a business account first');
      return;
    }

    setLoadingMetaTemplates(true);
    try {
      const response = await whatsappApi.listMetaTemplates(selectedBusinessAccountId, 50);
      const list = Array.isArray(response?.data) ? response.data : [];
      setMetaTemplates(
        list.map((item) => ({
          id: item.id,
          name: item.name,
          language: item.language,
          status: item.status,
          category: item.category,
          subCategory: item.subCategory,
          parameterFormat: item.parameterFormat,
          components: item.components,
        }))
      );
    } catch (error) {
      console.error('Failed to load Meta templates', error);
      toast.error('Failed to load Meta templates');
      setMetaTemplates([]);
    } finally {
      setLoadingMetaTemplates(false);
    }
  };

  const handleSaveTemplate = async (template) => {
    setSavingTemplateId(template.id);
    try {
      await whatsappApi.saveTemplate({
        businessAccountId: selectedBusinessAccountId,
        id: template.id,
        name: template.name || template.id,
        language: template.language || 'en_US',
        status: template.status || 'UNKNOWN',
        category: template.category || 'UNKNOWN',
        subCategory: template.subCategory || null,
        parameterFormat: template.parameterFormat || 'POSITIONAL',
        components: template.components || [],
      });
      await loadSavedTemplates(selectedBusinessAccountId);
      toast.success('Template saved');
    } catch (error) {
      console.error('Failed to save template', error);
      toast.error('Failed to save template');
    } finally {
      setSavingTemplateId('');
    }
  };

  const handleStartConversationFormChange = (field, value) => {
    setStartConversationForm((prev) => ({ ...prev, [field]: value }));
  };

  const normalizePhone = (value) => (value || '').replace(/\D/g, '');

  const toInternationalPhone = (value) => {
    const raw = (value || '').trim();
    if (!raw) return '';
    if (raw.startsWith('+')) {
      return `+${raw.slice(1).replace(/\D/g, '')}`;
    }
    if (raw.startsWith('00')) {
      return `+${raw.slice(2).replace(/\D/g, '')}`;
    }
    return `+${raw.replace(/\D/g, '')}`;
  };

  const handleStartConversationByPhone = async (rawPhone) => {
    if (!selectedPhoneNumberId) {
      return;
    }

    const recipient = toInternationalPhone(rawPhone);
    if (!recipient) {
      return;
    }

    const target = normalizePhone(recipient);
    setConversationSearch(target);

    const refreshedConversations = await loadConversations(selectedPhoneNumberId, target);
    const matched = refreshedConversations.find((item) => {
      const wa = normalizePhone(item.waId);
      return wa === target || wa.endsWith(target) || target.endsWith(wa);
    });

    if (matched) {
      setManualRecipientWaId('');
      setSelectedConversationId(matched.id);
      setMobilePanel('chat');
    } else {
      setSelectedConversationId('');
      setManualRecipientWaId(recipient);
      setMessages([]);
      setMobilePanel('chat');
    }
  };

  const handleStartConversation = async () => {
    if (!selectedPhoneNumberId) {
      toast.error('Select a saved phone number first');
      return;
    }

    const recipient = toInternationalPhone(startConversationForm.to);
    if (!recipient) {
      toast.error('Recipient phone number is required');
      return;
    }
    setStartConversationOpen(false);
    setStartConversationForm({ to: '' });

    await handleStartConversationByPhone(recipient);
  };

  const handleStartConversationProfileNameEdit = () => {
    if (!selectedConversation) {
      return;
    }

    setConversationProfileNameDraft(selectedConversation.profileName || '');
    setEditingConversationProfileName(true);
  };

  const handleCancelConversationProfileNameEdit = () => {
    setEditingConversationProfileName(false);
    setConversationProfileNameDraft('');
  };

  const handleSaveConversationProfileName = async () => {
    if (!selectedConversation) {
      return;
    }

    const profileName = conversationProfileNameDraft.trim();
    if (!profileName) {
      toast.error('Profile name is required');
      return;
    }

    setSavingConversationProfileName(true);
    try {
      const updatedConversation = await whatsappApi.updateConversationProfileName({
        conversationId: selectedConversation.id,
        profileName,
      });

      setConversations((prev) => prev.map((conversation) => (
        conversation.id === selectedConversation.id
          ? { ...conversation, ...(updatedConversation || {}), profileName }
          : conversation
      )));

      setEditingConversationProfileName(false);
      toast.success('Conversation profile name updated');
    } catch (error) {
      console.error('Failed to update conversation profile name', error);
      toast.error('Failed to update profile name');
    } finally {
      setSavingConversationProfileName(false);
    }
  };

  const sendTextMessageRequest = async ({
    phoneNumberId,
    conversationId,
    targetWaId,
    request,
    clientMessageId,
  }) => {
    const response = await whatsappApi.sendMessage({
      phoneNumberId,
      conversationId,
      request,
    });

    reconcileOptimisticMessage(clientMessageId, getSendResponseMessageMeta(response));
    updateConversationPreview(conversationId, {
      type: request.type,
      content: request,
      timestamp: new Date().toISOString(),
    }, targetWaId);
    return response;
  };

  const sendAttachmentMessageRequest = async ({
    phoneNumberId,
    conversationId,
    targetWaId,
    attachmentType,
    file,
    caption,
    contextMsgId,
    clientMessageId,
  }) => {
    const uploadResponse = await whatsappApi.uploadMedia({
      phoneNumberId,
      type: file.type || 'application/octet-stream',
      file,
    });

    const mediaPayload = { id: uploadResponse?.id };
    if (caption && attachmentType !== 'audio') {
      mediaPayload.caption = caption;
    }
    if (attachmentType === 'document') {
      mediaPayload.filename = file.name;
    }

    const request = {
      to: targetWaId,
      type: attachmentType,
      [attachmentType]: mediaPayload,
      contextMsgId: contextMsgId || undefined,
      context: contextMsgId ? { message_id: contextMsgId } : undefined,
    };

    const response = await whatsappApi.sendMessage({
      phoneNumberId,
      conversationId,
      request,
    });

    reconcileOptimisticMessage(clientMessageId, getSendResponseMessageMeta(response));
    updateConversationPreview(conversationId, {
      type: attachmentType,
      content: request,
      timestamp: new Date().toISOString(),
    }, targetWaId);
    return response;
  };

  const sendTemplateMessageRequest = async ({
    phoneNumberId,
    conversationId,
    targetWaId,
    request,
    clientMessageId,
  }) => {
    const response = await whatsappApi.sendTemplateMessage({
      phoneNumberId,
      conversationId,
      request,
    });

    if (clientMessageId) {
      reconcileOptimisticMessage(clientMessageId, getSendResponseMessageMeta(response));
      updateConversationPreview(conversationId, {
        type: request.type,
        content: request,
        timestamp: new Date().toISOString(),
      }, targetWaId);
    }

    return response;
  };

  const handleResendMessage = async (message) => {
    const retryMeta = message?.retryMeta;
    if (!retryMeta?.kind || !message?.clientMessageId) {
      return;
    }

    markOptimisticMessageAsSending(message.clientMessageId);
    setSendingMessage(true);

    try {
      if (retryMeta.kind === 'text') {
        await sendTextMessageRequest({
          phoneNumberId: retryMeta.phoneNumberId,
          conversationId: retryMeta.conversationId,
          targetWaId: retryMeta.targetWaId,
          request: retryMeta.request,
          clientMessageId: message.clientMessageId,
        });
      } else if (retryMeta.kind === 'attachment') {
        await sendAttachmentMessageRequest({
          phoneNumberId: retryMeta.phoneNumberId,
          conversationId: retryMeta.conversationId,
          targetWaId: retryMeta.targetWaId,
          attachmentType: retryMeta.attachmentType,
          file: retryMeta.file,
          caption: retryMeta.caption,
          contextMsgId: retryMeta.contextMsgId,
          clientMessageId: message.clientMessageId,
        });
      } else if (retryMeta.kind === 'template') {
        await sendTemplateMessageRequest({
          phoneNumberId: retryMeta.phoneNumberId,
          conversationId: retryMeta.conversationId,
          targetWaId: retryMeta.targetWaId,
          request: retryMeta.request,
          clientMessageId: message.clientMessageId,
        });
      }
    } catch (error) {
      console.error('Failed to resend WhatsApp message', error);
      markOptimisticMessageAsFailed(message.clientMessageId, error);
      toast.error(getErrorText(error, 'Failed to resend message'));
    } finally {
      setSendingMessage(false);
    }
  };

  const clearReplyState = () => {
    setReplyingToMessage(null);
    setActiveMessageMenuId('');
  };

  const handleReplyToMessage = (message) => {
    setReplyingToMessage({
      wamId: message.wamId,
      preview: getReplyPreviewText(message),
      direction: message.direction,
    });
    setActiveMessageMenuId('');
  };

  const cancelLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleMessageTouchStart = (message, event) => {
    if (!event.touches?.length) {
      return;
    }

    const touch = event.touches[0];
    setTouchStartByMessageId((prev) => ({
      ...prev,
      [message.wamId]: {
        x: touch.clientX,
        y: touch.clientY,
      },
    }));
    setSwipeOffsetByMessageId((prev) => ({
      ...prev,
      [message.wamId]: 0,
    }));

    cancelLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      setActiveMessageMenuId(message.wamId);
    }, 500);
  };

  const handleMessageTouchMove = (message, event) => {
    const start = touchStartByMessageId[message.wamId];
    if (!start || !event.touches?.length) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
      cancelLongPressTimer();
    }

    if (Math.abs(deltaY) > 48) {
      setSwipeOffsetByMessageId((prev) => ({
        ...prev,
        [message.wamId]: 0,
      }));
      return;
    }

    const positiveDeltaX = Math.max(0, deltaX);
    const clampedOffset = Math.min(positiveDeltaX, SWIPE_REPLY_MAX_OFFSET_PX);
    setSwipeOffsetByMessageId((prev) => ({
      ...prev,
      [message.wamId]: clampedOffset,
    }));
  };

  const handleMessageTouchEnd = (message) => {
    cancelLongPressTimer();

    const swipeOffset = swipeOffsetByMessageId[message.wamId] || 0;
    if (swipeOffset >= SWIPE_REPLY_TRIGGER_PX) {
      handleReplyToMessage(message);
    }

    setTouchStartByMessageId((prev) => {
      const next = { ...prev };
      delete next[message.wamId];
      return next;
    });
    setSwipeOffsetByMessageId((prev) => {
      const next = { ...prev };
      delete next[message.wamId];
      return next;
    });
  };

  const handleSendTextMessage = async () => {
    if (!selectedPhoneNumberId) {
      toast.error('Select a saved phone number first');
      return;
    }

    if (!canSendAnyMessage) {
      toast.error('Only templates can be sent for this conversation');
      return;
    }

    const body = messageDraft.trim();
    if (!body) {
      toast.error('Message text is required');
      return;
    }

    const targetWaId = selectedConversation?.waId;
    if (!targetWaId) {
      toast.error('Select a conversation first');
      return;
    }

    const timestamp = new Date().toISOString();
    const request = {
      to: targetWaId,
      type: 'text',
      text: {
        body,
        preview_url: false,
      },
      contextMsgId: replyingToMessage?.wamId || undefined,
      context: replyingToMessage?.wamId
        ? { message_id: replyingToMessage.wamId }
        : undefined,
    };

    const optimisticMessage = buildOptimisticOutgoingMessage({
      conversationId: selectedConversation?.id,
      type: 'text',
      content: {
        ...request,
        contextMsgPreview: replyingToMessage ? getReplyPreviewText(replyingToMessage) : undefined,
        contextMsgDirection: replyingToMessage?.direction || undefined,
      },
      contextMsgId: replyingToMessage?.wamId,
      timestamp,
      retryMeta: {
        kind: 'text',
        phoneNumberId: selectedPhoneNumberId,
        conversationId: selectedConversation?.id || null,
        targetWaId,
        request,
      },
    });

    const clientMessageId = appendOptimisticMessage(optimisticMessage, targetWaId);

    setSendingMessage(true);
    try {
      await sendTextMessageRequest({
        phoneNumberId: selectedPhoneNumberId,
        conversationId: selectedConversation?.id || null,
        targetWaId,
        request,
        clientMessageId,
      });

      setMessageDraft('');
      clearReplyState();
    } catch (error) {
      console.error('Failed to send WhatsApp message', error);
      markOptimisticMessageAsFailed(clientMessageId, error);
      toast.error(getErrorText(error, 'Failed to send message'));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOpenAttachmentPicker = (attachmentType) => {
    if (!canSendAnyMessage) {
      toast.error('Only templates can be sent for this conversation');
      return;
    }

    setPendingAttachmentType(attachmentType);
    setAttachmentMenuOpen(false);
    attachmentInputRef.current?.click();
  };

  const handleAttachmentSelection = (event) => {
    const file = event.target.files?.[0];
    const attachmentType = pendingAttachmentType;
    event.target.value = '';

    if (!file || !attachmentType) {
      setPendingAttachmentType('');
      return;
    }

    if (!selectedPhoneNumberId || !selectedConversation?.waId) {
      setPendingAttachmentType('');
      toast.error('Select a conversation first');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPendingAttachmentPreview({ file, type: attachmentType, objectUrl });
    setAttachmentMenuOpen(false);
  };

  const handleCancelAttachmentPreview = () => {
    if (pendingAttachmentPreview?.objectUrl) {
      URL.revokeObjectURL(pendingAttachmentPreview.objectUrl);
    }
    setPendingAttachmentPreview(null);
    setPendingAttachmentType('');
  };

  const handleSendAttachment = async () => {
    if (!pendingAttachmentPreview) return;

    const { file, type: attachmentType } = pendingAttachmentPreview;

    const caption = messageDraft.trim();
    const timestamp = new Date().toISOString();
    const optimisticContent = {
      [attachmentType]: {
        caption: caption && attachmentType !== 'audio' ? caption : undefined,
        filename: attachmentType === 'document' ? file.name : undefined,
      },
      contextMsgPreview: replyingToMessage ? getReplyPreviewText(replyingToMessage) : undefined,
      contextMsgDirection: replyingToMessage?.direction || undefined,
    };

    const optimisticMessage = buildOptimisticOutgoingMessage({
      conversationId: selectedConversation?.id,
      type: attachmentType,
      content: optimisticContent,
      contextMsgId: replyingToMessage?.wamId,
      timestamp,
      retryMeta: {
        kind: 'attachment',
        phoneNumberId: selectedPhoneNumberId,
        conversationId: selectedConversation?.id || null,
        targetWaId: selectedConversation?.waId,
        attachmentType,
        file,
        caption,
        contextMsgId: replyingToMessage?.wamId || null,
      },
    });

    const clientMessageId = appendOptimisticMessage(optimisticMessage, selectedConversation?.waId || '');

    setSendingMessage(true);
    try {
      await sendAttachmentMessageRequest({
        phoneNumberId: selectedPhoneNumberId,
        conversationId: selectedConversation?.id || null,
        targetWaId: selectedConversation.waId,
        attachmentType,
        file,
        caption,
        contextMsgId: replyingToMessage?.wamId || null,
        clientMessageId,
      });

      setMessageDraft('');
      clearReplyState();
      handleCancelAttachmentPreview();
    } catch (error) {
      console.error(`Failed to send WhatsApp ${attachmentType}`, error);
      markOptimisticMessageAsFailed(clientMessageId, error);
      toast.error(getErrorText(error, `Failed to send ${attachmentType}`));
    } finally {
      setPendingAttachmentType('');
      setSendingMessage(false);
    }
  };

  const handleSendTemplate = async () => {
    if (!selectedPhoneNumberId) {
      toast.error('Select a saved phone number first');
      return;
    }

    const targetWaId = selectedConversation?.waId || manualRecipientWaId;
    if (!targetWaId) {
      toast.error('Choose a conversation or open panel for a recipient first');
      return;
    }

    const template = savedTemplates.find((item) => item.id === selectedTemplateId);
    if (!template) {
      toast.error('Select a template first');
      return;
    }

    if (missingRequiredTemplateInputs) {
      toast.error('Complete all required template inputs first');
      return;
    }

    const components = [];

    if (templateRequirements.headerMediaType) {
      const mediaLink = templateInputs.headerMediaLink.trim();
      components.push({
        type: 'header',
        parameters: [{
          type: templateRequirements.headerMediaType,
          [templateRequirements.headerMediaType]: { link: mediaLink },
        }],
      });
    } else if (templateRequirements.headerLocationRequired) {
      components.push({
        type: 'header',
        parameters: [{
          type: 'location',
          location: {
            latitude: Number(templateInputs.headerLocation.latitude),
            longitude: Number(templateInputs.headerLocation.longitude),
            name: templateInputs.headerLocation.name || undefined,
            address: templateInputs.headerLocation.address || undefined,
          },
        }],
      });
    } else if (templateRequirements.headerTextPlaceholders.length > 0) {
      components.push({
        type: 'header',
        parameters: templateRequirements.headerTextPlaceholders.map((index) => ({
          type: 'text',
          text: String(templateInputs.headerTextParams[index] || templateRequirements.headerExampleTextParams[index] || ''),
        })),
      });
    }

    if (templateRequirements.bodyPlaceholders.length > 0) {
      components.push({
        type: 'body',
        parameters: templateRequirements.bodyPlaceholders.map((index) => ({
          type: 'text',
          text: String(templateInputs.bodyTextParams[index] || templateRequirements.bodyExampleTextParams[index] || ''),
        })),
      });
    }

    const request = {
      to: targetWaId,
      type: 'template',
      contextMsgId: replyingToMessage?.wamId || undefined,
      template: {
        name: template.name,
        language: {
          code: template.language || 'en_US',
        },
        components,
      },
    };

    const optimisticMessage = selectedConversation
      ? buildOptimisticOutgoingMessage({
        conversationId: selectedConversation.id,
        type: 'template',
        content: {
          template: {
            name: template.name,
            rendered: {
              header: applyTemplatePreview(
                templateRequirements.headerText,
                templateInputs.headerTextParams,
                templateRequirements.headerExampleTextParams
              ),
              body: applyTemplatePreview(
                templateRequirements.bodyText,
                templateInputs.bodyTextParams,
                templateRequirements.bodyExampleTextParams
              ),
              footer: templateRequirements.footerText,
            },
          },
        },
        contextMsgId: replyingToMessage?.wamId,
        timestamp: new Date().toISOString(),
        retryMeta: {
          kind: 'template',
          phoneNumberId: selectedPhoneNumberId,
          conversationId: selectedConversation.id,
          targetWaId,
          request,
        },
      })
      : null;

    const clientMessageId = optimisticMessage
      ? appendOptimisticMessage(optimisticMessage, targetWaId)
      : '';

    setSendingMessage(true);
    try {
      await sendTemplateMessageRequest({
        phoneNumberId: selectedPhoneNumberId,
        conversationId: selectedConversation?.id || null,
        targetWaId,
        request,
        clientMessageId,
      });

      clearReplyState();
      setSendTemplateOpen(false);
      setSelectedTemplateId('');
      if (!selectedConversation) {
        const refreshedConversations = await loadConversations(selectedPhoneNumberId);
        const target = normalizePhone(targetWaId);
        const matched = refreshedConversations.find((item) => {
          const wa = normalizePhone(item.waId);
          return wa === target || wa.endsWith(target) || target.endsWith(wa);
        });

        if (matched) {
          setSelectedConversationId(matched.id);
          setManualRecipientWaId('');
          await loadConversationMessages(matched.id);
        }
      }
    } catch (error) {
      console.error('Failed to send WhatsApp template', error);
      if (clientMessageId) {
        markOptimisticMessageAsFailed(clientMessageId, error);
      }
      toast.error(getErrorText(error, 'Failed to send template'));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleTemplateInputChange = (field, value, index = null) => {
    if (field === 'headerMediaLink') {
      setTemplateInputs((prev) => ({ ...prev, headerMediaLink: value }));
      return;
    }

    if (field === 'headerTextParam' && index != null) {
      setTemplateInputs((prev) => ({
        ...prev,
        headerTextParams: {
          ...prev.headerTextParams,
          [index]: value,
        },
      }));
      return;
    }

    if (field === 'headerLocation' && index) {
      setTemplateInputs((prev) => ({
        ...prev,
        headerLocation: {
          ...prev.headerLocation,
          [index]: value,
        },
      }));
      return;
    }

    if (field === 'bodyTextParam' && index != null) {
      setTemplateInputs((prev) => ({
        ...prev,
        bodyTextParams: {
          ...prev.bodyTextParams,
          [index]: value,
        },
      }));
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-100">
      <div className="flex h-full flex-col overflow-hidden lg:grid lg:flex-none lg:grid-cols-[380px_1fr]">
        <section className={`${mobilePanel === 'chat' ? 'hidden' : 'flex'} min-h-0 flex-1 flex-col border-r border-gray-200 bg-gray-50 lg:flex`}>
          <div className="space-y-3 border-b border-gray-200 px-3 py-3">
            <div className="flex items-center gap-2">
              <select
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedBusinessAccountId}
                onChange={(e) => setSelectedBusinessAccountId(e.target.value)}
              >
                {businessAccounts.length === 0 && <option value="">No accounts</option>}
                {businessAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setSettingsOpen(true)}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                title="WhatsApp settings"
              >
                <Cog6ToothIcon className="h-5 w-5" />
              </button>
            </div>

            <select
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedPhoneNumberId}
              onChange={(e) => setSelectedPhoneNumberId(e.target.value)}
            >
              {savedPhoneNumbers.length === 0 && <option value="">No saved phone numbers</option>}
              {savedPhoneNumbers.map((phone) => (
                <option key={phone.id} value={phone.id}>
                  {(phone.displayPhoneNumber || phone.id)}{phone.verifiedName ? ` - ${phone.verifiedName}` : ''}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <div className="flex flex-1 items-center rounded-lg border border-gray-300 bg-white px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500">
                <MagnifyingGlassIcon className="mr-2 h-4 w-4 text-gray-400" />
                <input
                  value={conversationSearch}
                  onChange={(e) => setConversationSearch(e.target.value)}
                  placeholder="Cauta in conversatii sau incepe o noua conversatie"
                  className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={() => setStartConversationOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-white hover:bg-emerald-600"
                title="Open conversation panel for phone number"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="text-[11px] text-gray-500">
              Settings are above conversations. Load and save phone numbers from the settings modal.
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto" onScroll={handleConversationsScroll}>
            {conversationsLoading && (
              <div className="p-4 text-sm text-gray-500">Loading conversations...</div>
            )}

            {!conversationsLoading && filteredConversations.length === 0 && (
              <div className="p-4 text-sm text-gray-500">No conversations yet for selected phone number.</div>
            )}

            {filteredConversations.map((conversation) => (
              <button
                key={conversation.id}
                className={`w-full border-b border-gray-200 px-4 py-3 text-left transition-colors hover:bg-gray-100 ${
                  selectedConversationId === conversation.id ? 'bg-blue-50' : ''
                }`}
                type="button"
                onClick={() => {
                  setManualRecipientWaId('');
                  setSelectedConversationId(conversation.id);
                  setMobilePanel('chat');
                }}
              >
                {(() => {
                  const windowState = getConversationWindowState(conversation.expirationTimestamp);
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900">{conversation.profileName || conversation.waId}</div>
                        <div className="text-xs text-gray-500">
                          {conversation.updatedAt ? new Date(conversation.updatedAt).toLocaleDateString() : ''}
                        </div>
                      </div>
                      <div className="mt-1 truncate text-sm text-gray-600">
                        {conversation.lastMessagePreview || 'No preview available'}
                      </div>
                      <div className={`mt-1 text-[11px] ${
                        windowState.isUnknown
                          ? 'text-gray-400'
                          : windowState.isOpen
                            ? 'text-emerald-600'
                            : 'text-amber-600'
                      }`}>
                        {windowState.label}
                      </div>
                    </>
                  );
                })()}
              </button>
            ))}

            {!conversationsLoading && loadingMoreConversations && (
              <div className="p-3 text-center text-xs text-gray-500">Loading more conversations...</div>
            )}
          </div>
        </section>

        <section className={`${mobilePanel === 'chat' ? 'flex' : 'hidden'} min-h-0 flex-1 flex-col bg-white lg:flex`}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <div className="min-w-0 flex flex-1 items-center gap-2 text-sm text-gray-700">
              <button
                type="button"
                onClick={() => setMobilePanel('list')}
                className="inline-flex items-center rounded px-1 py-0.5 text-xs text-gray-600 hover:bg-gray-100 lg:hidden"
                title="Back to conversations"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>

              <div className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-700">
                {getInitials(selectedConversation?.profileName || selectedConversation?.waId || manualRecipientWaId)}
              </div>

              <div className="min-w-0 flex-1">
                {selectedConversation && !editingConversationProfileName && (
                  <button
                    type="button"
                    onClick={handleStartConversationProfileNameEdit}
                    className="max-w-full truncate rounded px-1 py-0.5 text-left text-lg font-semibold text-gray-900 hover:bg-gray-100"
                    title="Edit conversation profile name"
                  >
                    {selectedConversation.profileName || selectedConversation.waId}
                  </button>
                )}

                {selectedConversation && editingConversationProfileName && (
                  <div className="flex items-center gap-2">
                    <input
                      value={conversationProfileNameDraft}
                      onChange={(e) => setConversationProfileNameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveConversationProfileName();
                        }
                        if (e.key === 'Escape') {
                          handleCancelConversationProfileNameEdit();
                        }
                      }}
                      className="w-full max-w-sm rounded-lg border border-gray-300 px-2 py-1 text-sm text-gray-900"
                      placeholder="Conversation profile name"
                      disabled={savingConversationProfileName}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleSaveConversationProfileName}
                      disabled={savingConversationProfileName}
                      className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {savingConversationProfileName ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelConversationProfileNameEdit}
                      disabled={savingConversationProfileName}
                      className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {!selectedConversation && (
                  <span className="truncate text-lg font-semibold text-gray-900">
                    {manualRecipientWaId ? `Recipient: ${manualRecipientWaId}` : 'No conversation selected'}
                  </span>
                )}
              </div>
            </div>

            <div className="relative lg:hidden">
              <button
                type="button"
                onClick={() => setMobileChatMenuOpen((prev) => !prev)}
                className="inline-flex items-center rounded p-1 text-gray-600 hover:bg-gray-100"
                title="Conversation details"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </button>

              {mobileChatMenuOpen && (
                <div className="absolute right-0 top-8 z-20 w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                  {selectedConversation ? (
                    <>
                      <div className="text-xs text-gray-500">{selectedConversation.waId}</div>
                      {(() => {
                        const windowState = getConversationWindowState(selectedConversation.expirationTimestamp);
                        return (
                          <div className={`mt-1 text-[11px] ${
                            windowState.isUnknown
                              ? 'text-gray-400'
                              : windowState.isOpen
                                ? 'text-emerald-600'
                                : 'text-amber-600'
                          }`}>
                            {windowState.label}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="text-xs text-gray-500">No history yet</div>
                  )}
                </div>
              )}
            </div>

            <div className="hidden text-right lg:block">
              {selectedConversation && (
                <>
                  <div className="text-xs text-gray-500">{selectedConversation.waId}</div>
                  {(() => {
                    const windowState = getConversationWindowState(selectedConversation.expirationTimestamp);
                    return (
                      <div className={`text-[11px] ${
                        windowState.isUnknown
                          ? 'text-gray-400'
                          : windowState.isOpen
                            ? 'text-emerald-600'
                            : 'text-amber-600'
                      }`}>
                        {windowState.label}
                      </div>
                    );
                  })()}
                </>
              )}
              {!selectedConversation && manualRecipientWaId && (
                <div className="text-xs text-gray-500">No history yet</div>
              )}
            </div>
          </div>

          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto bg-gray-50 px-5 py-4">
            {messagesLoading && (
              <div className="text-sm text-gray-500">Loading messages...</div>
            )}

            {!messagesLoading && !selectedConversation && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                {manualRecipientWaId ? 'No messages yet for this recipient.' : 'Select a conversation to view messages.'}
              </div>
            )}

            {!messagesLoading && selectedConversation && messages.length === 0 && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                No messages yet for this conversation.
              </div>
            )}

            {!messagesLoading && selectedConversation && messages.length > 0 && (
              <div className="space-y-3">
                {messages.map((message) => {
                  const isOutbound = message.direction === 'outbound';
                  const bubble = getMessageBubbleContent(message);
                  const messageStatus = String(message?.status || message?.content?.status || '').toLowerCase();
                  const sendState = String(message?.sendState || '').toLowerCase();
                  const isSending = sendState === 'sending' || messageStatus === 'sending';
                  const isFailed = sendState === 'failed' || messageStatus === 'failed';
                  const contextMsgId = message.contextMsgId || message?.content?.contextMsgId;
                  const contextMsgPreview = asNonEmptyString(message?.content?.contextMsgPreview);
                  const contextMsgDirection = String(message?.content?.contextMsgDirection || '').toLowerCase();
                  const originalWasOutbound = contextMsgDirection === 'outbound';
                  const timestampText = message.timestamp ? new Date(message.timestamp).toLocaleString() : '';
                  return (
                    <div
                      key={message.wamId}
                      className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        onTouchStart={(event) => handleMessageTouchStart(message, event)}
                        onTouchMove={(event) => handleMessageTouchMove(message, event)}
                        onTouchEnd={() => handleMessageTouchEnd(message)}
                        onTouchCancel={() => handleMessageTouchEnd(message)}
                        className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                          isOutbound ? 'bg-emerald-600 text-white' : 'bg-white text-gray-900 border border-gray-200'
                        } relative group transition-transform duration-150 ease-out`}
                        style={{
                          transform: `translateX(${swipeOffsetByMessageId[message.wamId] || 0}px)`,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setActiveMessageMenuId((prev) => (prev === message.wamId ? '' : message.wamId))}
                          className={`absolute right-2 top-2 rounded p-0.5 text-xs opacity-0 transition-opacity group-hover:opacity-100 ${
                            isOutbound ? 'text-emerald-100 hover:bg-emerald-700' : 'text-gray-500 hover:bg-gray-100'
                          }`}
                          title="Message actions"
                        >
                          <ChevronDownIcon className="h-3.5 w-3.5" />
                        </button>

                        {activeMessageMenuId === message.wamId && (
                          <div className="absolute right-2 top-8 z-20 rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
                            <button
                              type="button"
                              onClick={() => handleReplyToMessage(message)}
                              className="rounded px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
                            >
                              Reply
                            </button>
                          </div>
                        )}

                        {contextMsgId && contextMsgPreview && (
                          <div className={`mb-2 rounded-lg border-l-2 px-2 py-1 ${
                            isOutbound
                              ? 'border-emerald-100 bg-emerald-900/70'
                              : 'border-gray-500 bg-gray-200'
                          }`}>
                            <div className={`text-[10px] font-semibold ${
                              isOutbound ? 'text-emerald-100' : 'text-gray-700'
                            }`}>
                              {originalWasOutbound ? 'You' : (selectedConversation?.profileName || 'Contact')}
                            </div>
                            <div className={`line-clamp-2 text-xs ${
                              isOutbound ? 'text-emerald-100' : 'text-gray-800'
                            }`}>
                              {contextMsgPreview}
                            </div>
                          </div>
                        )}

                        {bubble.header && (
                          <div className={`mb-1 text-xs font-semibold ${isOutbound ? 'text-emerald-100' : 'text-gray-700'}`}>
                            {bubble.header}
                          </div>
                        )}

                        <div className="whitespace-pre-wrap">{bubble.body || bubble.fallbackLabel}</div>

                        {bubble.buttons.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {bubble.buttons.map((button) => (
                              <span
                                key={`${message.wamId}-${button}`}
                                className={`rounded-full border px-2 py-0.5 text-[11px] ${
                                  isOutbound
                                    ? 'border-emerald-200 text-emerald-100'
                                    : 'border-gray-300 text-gray-600'
                                }`}
                              >
                                {button}
                              </span>
                            ))}
                          </div>
                        )}

                        {bubble.footer && (
                          <div className={`mt-1 text-[11px] ${isOutbound ? 'text-emerald-100' : 'text-gray-500'}`}>
                            {bubble.footer}
                          </div>
                        )}

                        <div className={`mt-1 flex items-center gap-1 text-[11px] ${
                          isOutbound ? 'justify-end text-emerald-100' : 'text-gray-400'
                        }`}>
                          {timestampText && <span>{timestampText}</span>}
                          {isOutbound && isSending && <span>Sending...</span>}
                          {isOutbound && isFailed && <span className="text-amber-200">Failed</span>}
                          {isOutbound && !isSending && !isFailed && (
                            <MessageStatusTicks messageStatus={messageStatus} />
                          )}
                        </div>

                        {isOutbound && isFailed && (
                          <div className="mt-2 flex items-center justify-end gap-2">
                            {message.errorMessage && (
                              <span className="text-[11px] text-amber-100">
                                {message.errorMessage}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleResendMessage(message)}
                              disabled={sendingMessage}
                              className="rounded border border-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-50 hover:bg-emerald-700 disabled:opacity-50"
                            >
                              Resend
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 bg-white p-4">
            <input
              ref={attachmentInputRef}
              type="file"
              className="hidden"
              accept={ATTACHMENT_OPTIONS.find((option) => option.type === pendingAttachmentType)?.accept || '*'}
              onChange={handleAttachmentSelection}
            />

            <div className="space-y-3">
              {pendingAttachmentPreview && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {pendingAttachmentPreview.type}
                    </span>
                    <button
                      type="button"
                      onClick={handleCancelAttachmentPreview}
                      disabled={sendingMessage}
                      className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-200 disabled:opacity-50"
                    >
                      x
                    </button>
                  </div>

                  {pendingAttachmentPreview.type === 'image' && (
                    <img
                      src={pendingAttachmentPreview.objectUrl}
                      alt="Preview"
                      className="mb-2 max-h-48 w-full rounded-lg object-contain"
                    />
                  )}

                  {pendingAttachmentPreview.type === 'video' && (
                    <video
                      src={pendingAttachmentPreview.objectUrl}
                      controls
                      className="mb-2 max-h-48 w-full rounded-lg"
                    />
                  )}

                  {pendingAttachmentPreview.type === 'audio' && (
                    <audio
                      src={pendingAttachmentPreview.objectUrl}
                      controls
                      className="mb-2 w-full"
                    />
                  )}

                  {pendingAttachmentPreview.type === 'document' && (
                    <div className="mb-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                      <span className="truncate text-sm text-gray-700">{pendingAttachmentPreview.file.name}</span>
                    </div>
                  )}
                </div>
              )}

              {replyingToMessage && (
                <div className="flex items-start justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-emerald-700">
                      Replying to {replyingToMessage.direction === 'outbound' ? 'your message' : 'contact'}
                    </div>
                    <div className="truncate text-xs text-emerald-700">
                      {replyingToMessage.preview}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearReplyState}
                    className="rounded px-1.5 py-0.5 text-xs text-emerald-700 hover:bg-emerald-100"
                  >
                    x
                  </button>
                </div>
              )}

              {!canSendAnyMessage && (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs text-gray-500">
                    Use template messages for first contact unless the user has already replied.
                  </div>
                  <button
                    onClick={() => setSendTemplateOpen(true)}
                    disabled={!(selectedConversation || manualRecipientWaId) || sendingMessage || !savedTemplates.length}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {sendingMessage ? 'Sending...' : 'Send Template'}
                  </button>
                </div>
              )}

              {canSendAnyMessage && (
                <div className="relative">
                  {attachmentMenuOpen && (
                    <div className="absolute bottom-full left-0 mb-2 flex flex-wrap gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
                      {ATTACHMENT_OPTIONS.map((option) => (
                        <button
                          key={option.type}
                          type="button"
                          onClick={() => handleOpenAttachmentPicker(option.type)}
                          disabled={sendingMessage}
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {option.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => { setAttachmentMenuOpen(false); setSendTemplateOpen(true); }}
                        disabled={!(selectedConversation || manualRecipientWaId) || sendingMessage || !savedTemplates.length}
                        className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                      >
                        Template
                      </button>
                    </div>
                  )}

                  <div className="flex items-end gap-2">
                    <button
                      type="button"
                      onClick={() => setAttachmentMenuOpen((prev) => !prev)}
                      disabled={sendingMessage}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      title="Attach or send template"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>

                    <textarea
                      value={messageDraft}
                      onChange={(e) => setMessageDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (pendingAttachmentPreview) {
                            handleSendAttachment();
                          } else {
                            handleSendTextMessage();
                          }
                        }
                      }}
                      placeholder={pendingAttachmentPreview ? 'Type a caption' : 'Type a message'}
                      disabled={sendingMessage}
                      rows={1}
                      className="min-h-[40px] flex-1 resize-none rounded-2xl border border-gray-300 px-4 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />

                    <button
                      type="button"
                      onClick={pendingAttachmentPreview ? handleSendAttachment : handleSendTextMessage}
                      disabled={sendingMessage || (!pendingAttachmentPreview && !messageDraft.trim())}
                      className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Send
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <SettingsModal
        show={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        businessAccounts={businessAccounts}
        selectedBusinessAccountId={selectedBusinessAccountId}
        onSelectBusinessAccountId={setSelectedBusinessAccountId}
        showAddAccountForm={showAddAccountForm}
        onToggleAddAccountForm={() => setShowAddAccountForm((prev) => !prev)}
        addForm={addForm}
        onAddFormChange={handleAddFormChange}
        onAddBusinessAccount={handleAddBusinessAccount}
        addingBusinessAccount={addingBusinessAccount}
        metaPhoneNumbers={metaPhoneNumbers}
        loadingMetaPhoneNumbers={loadingMetaPhoneNumbers}
        onLoadMetaPhoneNumbers={handleLoadMetaPhoneNumbers}
        onPhoneDisplayNameChange={handlePhoneDisplayNameChange}
        onSavePhoneNumber={handleSavePhoneNumber}
        savingPhoneNumberId={savingPhoneNumberId}
        metaTemplates={metaTemplates}
        loadingMetaTemplates={loadingMetaTemplates}
        onLoadMetaTemplates={handleLoadMetaTemplates}
        onSaveTemplate={handleSaveTemplate}
        savingTemplateId={savingTemplateId}
      />

      <StartConversationModal
        show={startConversationOpen}
        onClose={() => setStartConversationOpen(false)}
        form={startConversationForm}
        onFormChange={handleStartConversationFormChange}
        onSubmit={handleStartConversation}
        submitting={sendingMessage}
      />

      <SendTemplateModal
        show={sendTemplateOpen}
        onClose={() => setSendTemplateOpen(false)}
        templates={savedTemplates}
        selectedTemplateId={selectedTemplateId}
        onSelectTemplateId={setSelectedTemplateId}
        selectedTemplate={selectedTemplate}
        requirements={templateRequirements}
        templateInputs={templateInputs}
        onTemplateInputChange={handleTemplateInputChange}
        missingRequiredInputs={missingRequiredTemplateInputs}
        onSubmit={handleSendTemplate}
        submitting={sendingMessage}
      />

      {loading && businessAccounts.length === 0 && (
        <div className="fixed bottom-4 right-4 rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg">
          Loading WhatsApp data...
        </div>
      )}
    </div>
  );
};

export default Whatsapp;


